/* eslint-disable react-hooks/exhaustive-deps */
import {
  parseAsArrayOf,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { CreatorGender } from '~/clientApi/creator.model';
import { CategoryTag as Vertical } from '~/components/CategoryTag/CategoryTag.model';
import {
  CreatorLocationDetail,
  ILocation,
  LocationFilter,
} from '~/screens/MyAgency/components/SearchLocation';
import { ageRangeFilter } from '~/screens/utils/ageRangeFilters';
import { COMPARATOR_TYPES, Filter } from '~/searchApi';
import { ICreatorLocation } from '~/types';

type ZustandState = {
  isCreatorsFiltersDrawerOpen: boolean;
  openCreatorsFiltersDrawer: () => void;
  closeCreatorsFiltersDrawer: () => void;
};

const useZustandState = create<ZustandState>((set) => ({
  isCreatorsFiltersDrawerOpen: false,
  openCreatorsFiltersDrawer: () =>
    set(() => ({ isCreatorsFiltersDrawerOpen: true })),
  closeCreatorsFiltersDrawer: () =>
    set(() => ({ isCreatorsFiltersDrawerOpen: false })),
}));

export const useTalentFiltersStore = () => {
  const zustandState = useZustandState();

  const [creatorGender, setCreatorGender] = useQueryState(
    'creatorGender',
    parseAsStringEnum<CreatorGender>(['Male', 'Female', 'Non Binary']),
  );

  const [creatorAge, setCreatorAge] = useQueryState('creatorAge');
  const [creatorLocation, setCreatorLocation] = useQueryState(
    'creatorLocation',
    parseAsJson<LocationFilter | null>(),
  );
  const [creatorVerticals, setCreatorVerticals] = useQueryState(
    'creatorVerticals',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const setVerticals = useCallback((verticalsList: Vertical[]) => {
    setCreatorVerticals(verticalsList.length > 0 ? verticalsList : null);
  }, []);

  const setGender = useCallback((gender: CreatorGender | null) => {
    setCreatorGender(gender);
  }, []);

  const setAge = useCallback((age: string | null) => {
    setCreatorAge(age);
  }, []);

  const setLocation = useCallback((location: ICreatorLocation) => {
    if (!location) return setCreatorLocation(null);

    const { city, country } = location as ILocation;
    // with FF foam_talent_location both location structures are supported, this TS assertion wont be necessary after removing the FF
    const regionName = (location as CreatorLocationDetail)?.regionName;
    const regionCode = (location as CreatorLocationDetail)?.regionCode;
    const countryCodeAlpha3 = (location as CreatorLocationDetail)
      ?.countryCodeAlpha3;

    setCreatorLocation({
      ...(city && { city }),
      ...(city && { regionCode }),
      ...(regionName && { regionName }),
      ...((city || regionName) && { countryCodeAlpha3 }),
      ...(country && { country }),
    } as LocationFilter);
  }, []);

  const clearFilter = useCallback(
    (
      filterType: 'verticals' | 'gender' | 'platform' | 'age' | 'location',
      value?: string,
    ) => {
      if (filterType === 'verticals') {
        const newVerticals = creatorVerticals.filter(
          (v: Vertical) => v !== value,
        );
        setVerticals(newVerticals as Vertical[]);
        return;
      }

      if (filterType === 'gender') {
        setGender(null);
        return;
      }

      if (filterType === 'age') {
        setAge(null);
        return;
      }

      if (filterType === 'location') {
        setLocation(null);
        return;
      }
    },
    [creatorVerticals.length],
  );

  const clearAllFilters = useCallback(async () => {
    setCreatorAge(null);
    setCreatorGender(null);
    setCreatorLocation(null);
    setCreatorVerticals(null);
  }, []);

  const locationAppliedFilter = useMemo((): Filter[] => {
    if (!creatorLocation) return [];

    let locationFilter = [];

    creatorLocation?.city &&
      locationFilter.push({
        name: 'location.city',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorLocation.city,
      });

    creatorLocation?.regionName &&
      locationFilter.push({
        name: 'location.region_name',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorLocation.regionName,
      });

    creatorLocation?.country &&
      locationFilter.push({
        name: 'location.country',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorLocation.country,
      });

    return locationFilter;
  }, [creatorLocation]);

  const locationAppliedFilterLabel = useMemo(() => {
    if (!creatorLocation) return null;

    const { city, country, regionCode, regionName, countryCodeAlpha3 } =
      creatorLocation;
    if (!city && !regionName && !country) return null;

    return [
      city,
      city ? regionCode : regionName,
      city || regionName ? countryCodeAlpha3 : country,
    ]
      .filter(Boolean)
      .join(', ');
  }, [creatorLocation]);

  const appliedFilters = useMemo((): Filter[] => {
    let applied = [];

    if (creatorVerticals.length) {
      applied.push(
        creatorVerticals.map((v) => ({
          name: 'category_agency',
          comparator: COMPARATOR_TYPES.EQ,
          value: v,
        })),
      );
    }

    if (creatorGender) {
      applied.push({
        name: 'gender_agency',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorGender,
      });
    }

    if (creatorAge) applied.push(ageRangeFilter(creatorAge));

    if (locationAppliedFilter) applied.push(locationAppliedFilter);

    return applied.flat();
  }, [creatorVerticals, creatorGender, creatorAge, creatorLocation]);

  const filtersSize = useMemo(() => {
    let total = 0;

    if (creatorVerticals.length > 0) total += creatorVerticals.length;
    if (creatorGender) total += 1;
    if (creatorAge) total += 1;
    if (locationAppliedFilter.length > 0) total += 1;

    return total;
  }, [creatorVerticals, creatorGender, creatorAge, locationAppliedFilter]);

  return {
    ...zustandState,
    verticals: creatorVerticals,
    setVerticals,
    gender: creatorGender,
    setGender,
    age: creatorAge,
    setAge,
    location: creatorLocation,
    locationAppliedFilter: locationAppliedFilterLabel,
    setLocation,
    creatorGender,
    creatorAge,
    creatorLocation,
    clearFilter,
    clearAllFilters,
    appliedFilters,
    filtersSize,
  };
};
